import React, { useState, useEffect, useRef, startTransition } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Checkbox,
  LinearProgress,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import Badge from "@mui/material/Badge";
import {
  LocationOn,
  Business,
  People,
  Assessment,
  CloudUpload,
  Search,
  Clear,
  Notifications,
  Store,
  ArrowBack,
  Fullscreen,
  FullscreenExit,
  Warning as ErrorIcon,
  Settings,
} from "@mui/icons-material";
import {
  MapContainer,
  TileLayer,
  useMap,
  Marker,
  GeoJSON,
  useMapEvents,
} from "react-leaflet";
import { Feature, GeoJsonObject, Geometry } from "geojson";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Company,
  assignRegionsToCompanies,
  processImportedCompanies,
  ImportedCompanyData,
  geocodeAndFindRegion,
  findRegionByCoordinates,
} from "../../utils/companyUtils";
import ImportDialog from "../../components/ImportDialog";
import ClusterMarkers from "../../components/ClusterMarkers";
import MeetingPointsMarkers from "../../components/MeetingPointsMarkers";
import { meetingPointsData } from "../../data/meetingPoints";
import { Mosaic } from "react-loading-indicators";

// Heatmap color scale based on company count - 10-level gradient from dark to light
const getHeatmapColor = (count: number): string => {
  if (count === 0) return "#090C37"; // Level 1 - Darkest
  if (count <= 20) return "#13174A"; // Level 2
  if (count <= 40) return "#1E225D"; // Level 3
  if (count <= 60) return "#283D70"; // Level 4
  if (count <= 80) return "#335984"; // Level 5
  if (count <= 100) return "#3D7597"; // Level 6
  if (count <= 120) return "#4891AA"; // Level 7
  if (count <= 140) return "#52ACBD"; // Level 8
  if (count <= 160) return "#5DC8D1"; // Level 9
  return "#67E4E4"; // Level 10 - Lightest (160+)
};

// Calculate company count per region based on actual dots displayed on map
const calculateRegionCompanyCounts = (
  companies: Company[],
  geoData: GeoJsonObject | null
): Record<string, number> => {
  const regionCounts: Record<string, number> = {};

  const geoDataWithFeatures = geoData as any;
  if (!geoDataWithFeatures || !geoDataWithFeatures.features) {
    return regionCounts;
  }

  // Initialize all regions with 0 count
  geoDataWithFeatures.features.forEach((feature: any) => {
    const regionId = String(
      feature.id || feature.properties?.OBJECTID || "unknown"
    );
    regionCounts[regionId] = 0;
  });

  // Count dots by grouping companies by coordinates (same as ClusterMarkers logic)
  const coordinateGroups = new Map<string, Company[]>();
  companies.forEach((company) => {
    const key = `${company.coordinates[0]},${company.coordinates[1]}`;
    if (!coordinateGroups.has(key)) {
      coordinateGroups.set(key, []);
    }
    coordinateGroups.get(key)!.push(company);
  });

  // Count dots per region (each coordinate group = 1 dot, but count all companies in that dot)
  coordinateGroups.forEach((groupCompanies, coordinateKey) => {
    // Find which region this coordinate belongs to
    const [lng, lat] = coordinateKey.split(",").map(Number);

    // Check if this coordinate is within any region
    geoDataWithFeatures.features.forEach((feature: any) => {
      const regionId = String(
        feature.id || feature.properties?.OBJECTID || "unknown"
      );

      // Use the company's regionId if available (more reliable than point-in-polygon)
      if (groupCompanies.some((company) => company.regionId === regionId)) {
        regionCounts[regionId] += groupCompanies.length; // Count all companies in this dot
        console.log(
          `🔍 Added ${groupCompanies.length} companies to region ${regionId}`
        );
      }
    });
  });

  // Ensure all regions have a count (even if 0)
  geoDataWithFeatures.features.forEach((feature: any) => {
    const regionId = String(
      feature.id || feature.properties?.OBJECTID || "unknown"
    );
    if (regionCounts[regionId] === undefined) {
      regionCounts[regionId] = 0;
    }
  });

  console.log("🔍 Region company counts (based on dots):", regionCounts);
  console.log("🔍 Total companies:", companies.length);
  console.log("🔍 Coordinate groups:", coordinateGroups.size);

  return regionCounts;
};

interface Region {
  id: string; // id of the region
  name: string; // name of the region
  nameEn: string; // name of the region in English
  coordinates: any; // coordinates of the region
  //  population: number;   // population of the region
  companies: Company[]; // companies of the region
  color: string; // color of the region
  lan_code?: string; // code of the region (optional)
  lan_name?: string; // name of the region (optional)
}
// Hàm để lấy màu dựa trên số lượng công ty
export const getCompanyCountColor = (companyCount: number): string => {
  // Bảng màu tối (sắc thái hải quân) để phù hợp với bản đồ mẫu
  // Sử dụng độ sáng tinh tế theo số lượng trong khi vẫn giữ cùng một sắc độ
  const base = 3; // nền tối hơn nữa
  const step = Math.min(4 + Math.floor(Math.min(companyCount, 1000) / 150), 15);
  const level = Math.min(base + step, 25);
  // Tạo màu hải quân đậm hơn bằng cách sử dụng mức độ đã tính
  const r = 4 + Math.floor(level * 0.15);
  const g = 6 + Math.floor(level * 0.2);
  const b = 20 + Math.floor(level * 0.7);
  return `rgb(${r}, ${g}, ${b})`;
};

// Function to get color intensity based on company count (0-1 scale)
export const getCompanyCountIntensity = (
  companyCount: number,
  maxCount: number = 1000
): number => {
  return Math.min(companyCount / maxCount, 1);
};

// Function to get gradient color based on company count (more sophisticated)
export const getCompanyCountGradientColor = (
  companyCount: number,
  maxCount: number = 1000
): string => {
  const intensity = Math.min(companyCount / maxCount, 1);

  // Create a smooth gradient from green to red
  const red = Math.floor(255 * intensity);
  const green = Math.floor(255 * (1 - intensity));
  const blue = 0;

  return `rgb(${red}, ${green}, ${blue})`;
};

// Map color tokens to match the sample image
const MAP_COLORS = {
  background: "#DFE3EA", // light gray sea like sample
  countryFill: "#ffffff", // dark indigo/purple-like land
  selectedFill: "#17163B", // same as land
  unselectedFill: "#140a33", // custom dark blue-black color for initial state
  border: "#034b89", // blue borders
  selectedBorder: "#034b89", // blue border for selected region
  fillOpacity: 0.85,
  selectedFillOpacity: 0.5,
  unselectedFillOpacity: 0.85,
};

const topicChipStyles = [
  {
    keywords: ["technologie", "technology"],
    styles: {
      backgroundColor: "rgba(52, 169, 54, 0.15)",
      color: "#5BE49A",
    },
  },
  {
    keywords: ["gesundheit", "health"],
    styles: {
      backgroundColor: "rgba(229, 76, 60, 0.18)",
      color: "#FF8A65",
    },
  },
  {
    keywords: ["sport", "kultur", "culture"],
    styles: {
      backgroundColor: "rgba(63, 81, 181, 0.18)",
      color: "#9FA8DA",
    },
  },
];

const membershipChipStyles = [
  {
    keywords: ["active personal", "apm"],
    styles: {
      backgroundColor: "rgba(0, 181, 173, 0.2)",
      color: "#4DD0E1",
    },
  },
  {
    keywords: ["active", "aim"],
    styles: {
      backgroundColor: "rgba(59, 130, 246, 0.2)",
      color: "#60A5FA",
    },
  },
  {
    keywords: ["projektpartner", "project"],
    styles: {
      backgroundColor: "rgba(255, 193, 7, 0.2)",
      color: "#FFD54F",
    },
  },
  {
    keywords: ["registered personal", "rpm"],
    styles: {
      backgroundColor: "rgba(156, 39, 176, 0.18)",
      color: "#CE93D8",
    },
  },
  {
    keywords: ["registered", "rim"],
    styles: {
      backgroundColor: "rgba(156, 39, 176, 0.18)",
      color: "#CE93D8",
    },
  },
];

const getColoredChipStyles = (
  value: string | undefined,
  palette: { keywords: string[]; styles: Record<string, string> }[]
) => {
  if (!value) return {};
  const normalized = value.toLowerCase();
  const match = palette.find((entry) =>
    entry.keywords.some((keyword) => normalized.includes(keyword))
  );
  return (
    match || {
      styles: {
        backgroundColor: "rgba(255,255,255,0.1)",
        color: "rgba(255,255,255,0.85)",
      },
    }
  ).styles;
};

// Component to handle GeoJSON layer using useEffect and direct Leaflet manipulation
function GeoJSONLayer({
  data,
  onRegionClick,
  selectedRegionId,
  onCompanyClick,
  showCompanies,
  companies,
  onRegionSelect,
  regionCompanyCounts,
}: {
  data: GeoJsonObject | null;
  onRegionClick?: (region: Region) => void;
  onCompanyClick?: (company: Company) => void;
  selectedRegionId?: string;
  showCompanies?: boolean;
  companies?: Company[];
  onRegionSelect?: (region: Region) => void;
  regionCompanyCounts?: Record<string, number>;
}) {
  const map = useMap();
  const layerRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  useEffect(() => {
    if (!data) {
      console.log("❌ No data provided to GeoJSONLayer");
      return;
    }

    console.log("🔄 GeoJSONLayer received data:", data);
    console.log("Data type:", data.type);
    console.log("Features count:", (data as any).features?.length);
    console.log("First feature:", (data as any).features?.[0]);

    // Clear existing layers
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
    }
    markersRef.current.forEach((marker) => map.removeLayer(marker));
    markersRef.current = [];

    // Create GeoJSON layer using Leaflet directly
    console.log("🔄 Creating Leaflet GeoJSON layer...");
    const geoJSONLayer = (L as any).geoJSON(data as any, {
      style: (feature: Feature) => {
        const properties = feature.properties || {};
        const regionId = String(
          properties.OBJECTID || properties.id || properties.iso || ""
        );
        const isSelected = selectedRegionId === regionId;

        const regionName =
          properties.lan_name || properties.kmr || properties.name || "Unknown";

        const currentRegionId = String(
          properties.OBJECTID || properties.id || ""
        );
        const regionCompanies =
          companies?.filter(
            (company) =>
              company.regionId === currentRegionId ||
              company.region?.toLowerCase() === regionName.toLowerCase()
          ) || [];
        const companyCount = regionCompanies.length;
        // Choose fill color based on selection state
        const fillColor = isSelected
          ? MAP_COLORS.selectedFill
          : MAP_COLORS.unselectedFill;

        return {
          color: isSelected ? MAP_COLORS.selectedBorder : MAP_COLORS.border,
          weight: isSelected ? 3 : 1.5,
          fillColor: fillColor,
          fillOpacity: isSelected
            ? MAP_COLORS.selectedFillOpacity
            : MAP_COLORS.unselectedFillOpacity,
        };
      },
      onEachFeature: (feature: Feature, layer: any) => {
        layer.on("click", (e: any) => {
          console.log("🖱️ Region clicked!", e);
          const properties = feature.properties || {};
          const regionName =
            properties.lan_name ||
            properties.kmr ||
            properties.name ||
            "Unknown";

          console.log("📍 Region name:", regionName);

          if (regionName) {
            const regionCompanies =
              companies?.filter(
                (company) =>
                  company.region?.toLowerCase() === regionName.toLowerCase()
              ) || [];
            const companyCount = regionCompanies.length;
            const color = getCompanyCountColor(companyCount);

            // Get coordinates from GeoJSON geometry
            let coordinates: any = null;
            if (
              feature.geometry.type === "Polygon" ||
              feature.geometry.type === "MultiPolygon"
            ) {
              coordinates = (feature.geometry as any).coordinates;
            }

            const regionData: Region = {
              id: String(properties.id || `region-${Math.random()}`),
              name: regionName,
              nameEn: regionName,
              coordinates: coordinates || [],
              companies: regionCompanies,
              color: color,
              lan_code: properties.lan_code || "",
              lan_name: regionName,
            };

            if (onRegionSelect) {
              onRegionSelect(regionData);
            }

            const region: Region = {
              id: String(properties.id || `region-${Math.random()}`),
              name: regionName,
              nameEn: regionName,
              coordinates: coordinates || [],
              companies: regionCompanies,
              color: getCompanyCountColor(companyCount),
              lan_code: properties.lan_code || "",
              lan_name: regionName,
            };

            if (onRegionClick) {
              onRegionClick(region);
            }
          }
        });
      },
    });

    try {
      geoJSONLayer.addTo(map);
      layerRef.current = geoJSONLayer;
      console.log("✅ GeoJSON layer added to map successfully");
      console.log("Layer bounds:", geoJSONLayer.getBounds());
    } catch (error) {
      console.error("❌ Error adding GeoJSON layer to map:", error);
    }

    // Add company markers if needed
    if (showCompanies && companies && companies.length > 0) {
      companies.forEach((company) => {
        const marker = (L as any).marker(
          [company.coordinates[1], company.coordinates[0]],
          {
            icon: (L as any).divIcon({
              className: "company-marker",
              html: `<div style="
              background-color: white;
              width: 14px;
              height: 14px;
              border-radius: 50%;
              border: 2px solid #808080;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            "></div>`,
              iconSize: [14, 14],
              iconAnchor: [7, 7],
            }),
          }
        );

        marker.bindPopup(`
          <div>
            <h4>${company.name}</h4>
            <p><strong>Address:</strong> ${company.address}</p>
            <p><strong>Region:</strong> ${company.region || "Unknown"}</p>
          </div>
        `);

        marker.on("click", () => {
          if (onCompanyClick) {
            onCompanyClick(company);
          }
        });

        marker.addTo(map);
        markersRef.current.push(marker);
      });
    }

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
      }
      markersRef.current.forEach((marker) => map.removeLayer(marker));
      markersRef.current = [];
    };
  }, [
    data,
    map,
    onRegionClick,
    selectedRegionId,
    onCompanyClick,
    showCompanies,
    companies,
    regionCompanyCounts,
  ]);

  return null;
}

// Alternative component using useMapEvents and direct Leaflet manipulation
function AlternativeGeoJSONLayer({
  data,
  onRegionClick,
  selectedRegionId,
  onCompanyClick,
  showCompanies,
  companies,
  onRegionSelect,
  regionCompanyCounts,
}: {
  data: GeoJsonObject | null;
  onRegionClick?: (region: Region) => void;
  onCompanyClick?: (company: Company) => void;
  selectedRegionId?: string;
  showCompanies?: boolean;
  companies?: Company[];
  onRegionSelect?: (region: Region) => void;
  regionCompanyCounts?: Record<string, number>;
}) {
  const map = useMap();
  const layerRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  // Handle map events
  useMapEvents({
    click: (e: any) => {
      console.log("Map clicked at:", e.latlng);
    },
  });

  useEffect(() => {
    if (!data) return;

    // Clear existing layers
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
    }
    markersRef.current.forEach((marker) => map.removeLayer(marker));
    markersRef.current = [];

    // Create GeoJSON layer using Leaflet directly
    const geoJSONLayer = (L as any).geoJSON(data as any, {
      style: (feature: Feature) => {
        const properties = feature.properties || {};
        const regionId = String(
          properties.OBJECTID || properties.id || properties.iso || ""
        );
        const isSelected = selectedRegionId === regionId;
        console.log(
          "Style check - regionId:",
          regionId,
          "selectedRegionId:",
          selectedRegionId,
          "isSelected:",
          isSelected
        );

        const regionName =
          properties.lan_name || properties.kmr || properties.name || "Unknown";

        const currentRegionId = String(
          properties.OBJECTID || properties.id || ""
        );

        // Use the pre-calculated region company counts (based on unique addresses)
        const companyCount =
          (regionCompanyCounts && regionCompanyCounts[currentRegionId]) || 0;
        console.log(
          `🔍 Region ${currentRegionId} company count:`,
          companyCount
        );
        console.log(
          `🔍 Region ${currentRegionId} heatmap color:`,
          getHeatmapColor(companyCount)
        );

        // Always use heatmap color based on company count (including 0 = darkest color)
        // This ensures regions with no companies show the darkest color (#090C37)
        const fillColor = isSelected
          ? MAP_COLORS.selectedFill
          : getHeatmapColor(companyCount);

        return {
          color: isSelected ? MAP_COLORS.selectedBorder : MAP_COLORS.border,
          weight: isSelected ? 3 : 1.5,
          fillColor: fillColor,
          fillOpacity: isSelected
            ? MAP_COLORS.selectedFillOpacity
            : MAP_COLORS.unselectedFillOpacity,
        };
      },
      onEachFeature: (feature: Feature, layer: any) => {
        layer.on("click", (e: any) => {
          const properties = feature.properties || {};
          const regionName =
            properties.KMR ||
            properties.lan_name ||
            properties.kmr ||
            properties.name ||
            "Unknown";

          console.log("Region clicked:", regionName, properties);

          if (regionName && onRegionClick) {
            const regionId = String(
              properties.OBJECTID || properties.id || Math.random()
            );
            const regionCompanies =
              companies?.filter(
                (company) =>
                  company.regionId === regionId ||
                  company.region?.toLowerCase() === regionName.toLowerCase()
              ) || [];

            const region: Region = {
              id: String(properties.OBJECTID || properties.id || Math.random()),
              name: regionName,
              nameEn: regionName,
              coordinates: (feature.geometry as any).coordinates || [],
              companies: regionCompanies,
              color: getCompanyCountColor(regionCompanies.length),
              lan_code: String(properties.OBJECTID || properties.id),
              lan_name: regionName,
            };

            onRegionClick(region);
          }
        });
      },
    });

    try {
      geoJSONLayer.addTo(map);
      layerRef.current = geoJSONLayer;
      console.log("✅ GeoJSON layer added to map successfully");
      console.log("Layer bounds:", geoJSONLayer.getBounds());
    } catch (error) {
      console.error("❌ Error adding GeoJSON layer to map:", error);
    }

    // Add company markers if needed
    if (showCompanies && companies && companies.length > 0) {
      companies.forEach((company) => {
        const marker = (L as any).marker(
          [company.coordinates[1], company.coordinates[0]],
          {
            icon: (L as any).divIcon({
              className: "company-marker",
              html: `<div style="
              background-color: white;
              width: 14px;
              height: 14px;
              border-radius: 50%;
              border: 2px solid #808080;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            "></div>`,
              iconSize: [14, 14],
              iconAnchor: [7, 7],
            }),
          }
        );

        marker.bindPopup(`
          <div>
            <h4>${company.name}</h4>
            <p><strong>Address:</strong> ${company.address}</p>
            <p><strong>Region:</strong> ${company.region || "Unknown"}</p>
          </div>
        `);

        marker.on("click", () => {
          if (onCompanyClick) {
            onCompanyClick(company);
          }
        });

        marker.addTo(map);
        markersRef.current.push(marker);
      });
    }

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
      }
      markersRef.current.forEach((marker) => map.removeLayer(marker));
      markersRef.current = [];
    };
  }, [
    data,
    map,
    onRegionClick,
    selectedRegionId,
    onCompanyClick,
    showCompanies,
    companies,
    regionCompanyCounts,
  ]);

  return null;
}

// Force Leaflet to recalculate size when layout changes (e.g., sidebar collapse)
function MapResizeInvalidator({ dependency }: { dependency: any }) {
  const map = useMap();
  useEffect(() => {
    const leafletMap = map as any;

    const invokeResize = () => {
      try {
        if (!leafletMap?._loaded) {
          return;
        }
        const mapPane = leafletMap?._mapPane;
        if (!mapPane || typeof mapPane._leaflet_pos === "undefined") {
          return;
        }
        leafletMap.invalidateSize();
      } catch (e) {
        console.warn("Map resize error:", e);
      }
    };

    const timer = setTimeout(() => {
      if (leafletMap?._loaded) {
        invokeResize();
      } else {
        leafletMap?.once("load", invokeResize);
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      leafletMap?.off("load", invokeResize);
    };
  }, [dependency, map]);
  return null;
}

// Component to handle map bounds fitting
function MapBoundsFitter({ geoData }: { geoData: GeoJsonObject | null }) {
  const map = useMap();

  useEffect(() => {
    if (!geoData || geoData.type !== "FeatureCollection") return;

    const doFit = () => {
      try {
        const leafletMap = map as any;
        if (
          !leafletMap?._mapPane ||
          typeof leafletMap._mapPane._leaflet_pos === "undefined"
        ) {
          return;
        }
        const geoJSONLayer = (L as any).geoJSON(geoData as any);
        const bounds = geoJSONLayer.getBounds();
        if (!bounds || !bounds.isValid()) return;
        leafletMap.fitBounds(bounds, {
          padding: [30, 30],
          maxZoom: 8,
        });
      } catch (e) {
        // silently ignore to avoid runtime crash during transitions
      }
    };

    if ((map as any)?._loaded) {
      doFit();
    } else {
      (map as any).once("load", doFit);
    }
  }, [geoData, map]);

  return null;
}

// Component to restrict map to Germany bounds
function MapBoundsRestrictor() {
  const map = useMap();

  useEffect(() => {
    // Germany bounds: [south, west] to [north, east]
    const germanyBounds = [
      [47.2, 5.8], // Southwest corner
      [55.1, 15.2], // Northeast corner
    ];

    // Set max bounds to restrict movement outside Germany
    (map as any).setMaxBounds(germanyBounds);

    // Set max bounds viscosity to prevent dragging outside bounds
    (map as any).options.maxBoundsViscosity = 1.0;

    // Set minimum zoom level
    (map as any).options.minZoom = 6;
    (map as any).options.maxZoom = 25;

    // Cleanup
    return () => {
      // Reset bounds when component unmounts
      (map as any).setMaxBounds(null);
    };
  }, [map]);

  return null;
}

interface GermanyMapPageProps {
  drawerOpen?: boolean;
}

const GermanyMapPage = ({ drawerOpen = false }: GermanyMapPageProps) => {
  const [selectedRegion, setSelectedRegion] = useState<any>(null);
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showCompanies] = useState(true); // Always show companies
  const [geoData, setGeoData] = useState<GeoJsonObject | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMeetingPoints, setIsLoadingMeetingPoints] = useState(false);

  // Load companies from localStorage or default to empty array
  const [companies, setCompaniesState] = useState<Company[]>(() => {
    try {
      const savedCompanies = localStorage.getItem("importedCompanies");
      return savedCompanies ? JSON.parse(savedCompanies) : [];
    } catch (error) {
      console.error("Error loading companies from localStorage:", error);
      return [];
    }
  });

  // Wrapper function to save companies to localStorage (excluding meeting points)
  const setCompanies = (
    value: Company[] | ((prev: Company[]) => Company[])
  ) => {
    setCompaniesState((prev) => {
      const newCompanies = typeof value === "function" ? value(prev) : value;
      // Save only regular companies (exclude meeting points)
      const regularCompanies = newCompanies.filter(
        (c) => !c.id.startsWith("meeting-")
      );
      try {
        localStorage.setItem(
          "importedCompanies",
          JSON.stringify(regularCompanies)
        );
      } catch (error) {
        console.error("Error saving companies to localStorage:", error);
      }
      return newCompanies;
    });
  };

  const [meetingPoints, setMeetingPoints] = useState<Company[]>([]); // Separate state for meeting points
  const [isSideCollapsed, setIsSideCollapsed] = useState(false);

  // Load company visibility from localStorage
  const [companyVisibility, setCompanyVisibilityState] = useState<
    Record<string, boolean>
  >(() => {
    try {
      const savedVisibility = localStorage.getItem("companyVisibility");
      return savedVisibility ? JSON.parse(savedVisibility) : {};
    } catch (error) {
      console.error(
        "Error loading company visibility from localStorage:",
        error
      );
      return {};
    }
  });

  // Wrapper function to save company visibility to localStorage
  const setCompanyVisibility = (
    value:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>)
  ) => {
    setCompanyVisibilityState((prev) => {
      const newVisibility = typeof value === "function" ? value(prev) : value;
      try {
        localStorage.setItem(
          "companyVisibility",
          JSON.stringify(newVisibility)
        );
      } catch (error) {
        console.error(
          "Error saving company visibility to localStorage:",
          error
        );
      }
      return newVisibility;
    });
  };
  const [regionCompanyCounts, setRegionCompanyCounts] = useState<
    Record<string, number>
  >({});

  // State for Material-UI Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRegion, setDialogRegion] = useState<Region | null>(null);

  // State for Import Dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");

  // State for Failed Geocoding Modal
  const [failedGeocodingDialogOpen, setFailedGeocodingDialogOpen] =
    useState(false);
  const [failedCompanies, setFailedCompanies] = useState<ImportedCompanyData[]>(
    []
  );

  // State for Search
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);

  // State for Settings Menu
  const [settingsMenuAnchor, setSettingsMenuAnchor] =
    useState<null | HTMLElement>(null);

  // Load dark mode from localStorage or default to true
  const [darkMode, setDarkModeState] = useState(() => {
    const savedDarkMode = localStorage.getItem("darkMode");
    return savedDarkMode !== null ? JSON.parse(savedDarkMode) : true;
  });

  // Wrapper function to save dark mode to localStorage
  const setDarkMode = (value: boolean) => {
    setDarkModeState(value);
    localStorage.setItem("darkMode", JSON.stringify(value));
  };

  // Load language from localStorage or default to 'en'
  const [language, setLanguageState] = useState<'en' | 'de'>(() => {
    const savedLanguage = localStorage.getItem("language");
    return (savedLanguage === 'de' || savedLanguage === 'en') ? savedLanguage : 'en';
  });

  // Wrapper function to save language to localStorage
  const setLanguage = (value: 'en' | 'de') => {
    setLanguageState(value);
    localStorage.setItem("language", value);
  };

  // Translations
  const translations = React.useMemo(() => ({
    en: {
      darkMode: "Dark Mode",
      language: "Language",
      english: "English",
      german: "Deutsch",
      importCompanies: "Import Companies",
      searchCompanies: "Search Companies",
      searchPlaceholder: "Search by name, address, or region...",
      companies: "Companies",
      showAll: "Show All",
      hideAll: "Hide All",
      searchResults: "Search Results",
      companiesFound: "companies found",
      noCompaniesFound: "No companies found matching",
      regionDetails: "Region Details",
      region: "Region",
      close: "Close",
      companyCount: "Company Count",
      loadingMap: "Loading map...",
    },
    de: {
      darkMode: "Dunkler Modus",
      language: "Sprache",
      english: "Englisch",
      german: "Deutsch",
      importCompanies: "Unternehmen importieren",
      searchCompanies: "Unternehmen suchen",
      searchPlaceholder: "Suche nach Name, Adresse oder Region...",
      companies: "Unternehmen",
      showAll: "Alle anzeigen",
      hideAll: "Alle ausblenden",
      searchResults: "Suchergebnisse",
      companiesFound: "Unternehmen gefunden",
      noCompaniesFound: "Keine Unternehmen gefunden für",
      regionDetails: "Regionsdetails",
      region: "Region",
      close: "Schließen",
      companyCount: "Unternehmensanzahl",
      loadingMap: "Karte wird geladen...",
    },
  }), []);

  const t = React.useMemo(() => translations[language], [language, translations]);

  const shouldDisplayByDefault = (company: Company) => {
    const flag = company.displayed?.toString().toLowerCase().trim();
    if (!flag) return true;
    if (["no", "false", "0", "off"].includes(flag)) {
      return false;
    }
    return true;
  };

  // Handle import companies from Excel
  const handleImportCompanies = async (
    importedData: ImportedCompanyData[]
  ): Promise<void> => {
    if (!geoData) {
      throw new Error("No geo data available for region detection");
    }

    setIsImporting(true);
    setImportProgress(0);
    setImportStatus("Starting import...");

    try {
      console.log(`🔄 Importing ${importedData.length} companies...`);

      // Simulate progress that matches actual import time
      const totalSteps = 20; // Total progress steps
      const stepInterval = 150; // Update every 150ms
      let currentStep = 0;

      const progressInterval = setInterval(() => {
        currentStep++;
        const progress = Math.min((currentStep / totalSteps) * 99, 99); // Stop at 99%
        setImportProgress(progress);

        if (currentStep >= totalSteps) {
          clearInterval(progressInterval);
        }
      }, stepInterval);

      setImportStatus("Processing company data...");

      // Process imported companies with geocoding and region finding
      const { companies: processedCompanies, failedCompanies } =
        await processImportedCompanies(importedData, geoData);

      // Complete progress smoothly
      setImportProgress(99);
      setImportStatus("Import completed successfully!");

      // Reset progress after showing completion
      setTimeout(() => {
        setImportProgress(0);
        setImportStatus("");
      }, 2000);

      // Add to existing companies (preserve meeting points)
      setCompanies((prevCompanies) => {
        // Filter out any duplicates and preserve meeting points
        const existingIds = new Set(prevCompanies.map((c) => c.id));
        const newCompanies = processedCompanies.filter(
          (c) => !existingIds.has(c.id)
        );
        return [...prevCompanies, ...newCompanies];
      });

      // Initialize visibility for new companies (all visible by default)
      const newVisibility: Record<string, boolean> = {};
      processedCompanies.forEach((company) => {
        newVisibility[company.id] = shouldDisplayByDefault(company);
      });
      setCompanyVisibility((prev) => ({ ...prev, ...newVisibility }));

      console.log(
        `✅ Successfully imported ${processedCompanies.length} companies`
      );

      // Show modal if there are failed companies
      if (failedCompanies.length > 0) {
        setFailedCompanies(failedCompanies);
        setFailedGeocodingDialogOpen(true);
      }

      // Reset progress after delay
      setTimeout(() => {
        setImportProgress(0);
        setImportStatus("");
      }, 2000);
    } catch (error) {
      console.error("Error importing companies:", error);
      setImportStatus("Import failed!");
      throw error; // Re-throw để ImportDialog có thể handle
    } finally {
      setIsImporting(false);
    }
  };

  // Load GeoJSON data first (show map immediately)
  useEffect(() => {
    const loadGeoJSONData = async () => {
      try {
        console.log("🔄 Loading German regions data...");
        setIsLoading(true);

        // Load test1.json (now in standard GeoJSON format)
        const response = await fetch("/test1.json");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const rawData = await response.json();

        // Remove inner regions: "Verband Region Stuttgart" (OBJECTID: 14) and "Metropolregion Mitteldeutschland" (OBJECTID: 5)
        // These are smaller inner regions that should be removed
        const filteredFeatures = rawData.filter((feature: any) => {
          const objectId = feature.properties?.OBJECTID;
          const kmr = feature.properties?.KMR;
          // Remove "Verband Region Stuttgart" (OBJECTID: 14) and "Metropolregion Mitteldeutschland" (OBJECTID: 5)
          return (
            objectId !== 14 &&
            objectId !== 5 &&
            kmr !== "Verband Region Stuttgart" &&
            kmr !== "Metropolregion Mitteldeutschland"
          );
        });

        console.log(`🗑️ Removed inner regions: Verband Region Stuttgart, Metropolregion Mitteldeutschland`);
        console.log(`📊 Regions: ${rawData.length} -> ${filteredFeatures.length}`);

        // Convert array of features to FeatureCollection
        const geoJSONData = {
          type: "FeatureCollection" as const,
          features: filteredFeatures,
        };

        console.log("✅ Loaded test1.json (Standard GeoJSON)");
        console.log("GeoJSON structure:", geoJSONData);
        console.log("Features count:", geoJSONData.features?.length || 0);
        console.log("First feature:", geoJSONData.features?.[0]);

        setGeoData(geoJSONData);

        // Companies are already loaded from localStorage, don't reset them
        // Only imported companies will be added via handleImportCompanies

        // Show map immediately, don't wait for meeting points
        setIsLoading(false);
      } catch (error) {
        console.error("❌ Error loading data:", error);
        setIsLoading(false);
      }
    };

    loadGeoJSONData();
  }, []);

  // Load meeting points after map is shown (instant - using pre-geocoded coordinates)
  useEffect(() => {
    if (!geoData) return; // Wait for geoData to be loaded first

    const loadMeetingPoints = async () => {
      try {
        setIsLoadingMeetingPoints(true);
        console.log("🔄 Loading meeting points...");

        // Use pre-geocoded coordinates - no API calls needed!
        const meetingPointCompanies: Company[] = meetingPointsData.map(
          (meetingPoint) => {
            // Find region using coordinates
            const regionInfo = findRegionByCoordinates(
              meetingPoint.coordinates,
              geoData
            );

            const company: Company = {
              id: meetingPoint.id,
              name: meetingPoint.name,
              address: meetingPoint.address,
              coordinates: meetingPoint.coordinates,
              region: regionInfo?.region || meetingPoint.metropolitanRegion,
              regionId: regionInfo?.regionId || "unknown",
            };

            console.log(
              `✅ Meeting point: ${meetingPoint.name} -> ${meetingPoint.coordinates[1]}, ${meetingPoint.coordinates[0]} (${company.region})`
            );
            return company;
          }
        );

        console.log(
          `✅ Loaded ${meetingPointCompanies.length} meeting points successfully`
        );

        // Set meeting points separately (only for map display, not in list)
        setMeetingPoints(meetingPointCompanies);
        setIsLoadingMeetingPoints(false);
      } catch (error) {
        console.error("❌ Error loading meeting points:", error);
        setIsLoadingMeetingPoints(false);
      }
    };

    loadMeetingPoints();
  }, [geoData]);

  // Filter companies based on search term and visibility (exclude meeting points from list)
  useEffect(() => {
    // Filter out meeting points from companies list (they only show on map)
    const regularCompanies = companies.filter(
      (company) => !company.id.startsWith("meeting-")
    );

    let filtered = regularCompanies;

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = regularCompanies.filter(
        (company) =>
          company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (company.region &&
            company.region.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by visibility
    const visibleCompanies = filtered.filter(
      (company) => companyVisibility[company.id] !== false
    );

    console.log(
      "🔍 Filtering companies - searchTerm:",
      searchTerm,
      "companies:",
      regularCompanies.length,
      "filtered:",
      filtered.length,
      "visible:",
      visibleCompanies.length
    );
    console.log("🔍 Company visibility:", companyVisibility);

    startTransition(() => {
      setFilteredCompanies(visibleCompanies);
    });
  }, [searchTerm, companies, companyVisibility]);

  // Calculate region company counts for heatmap
  useEffect(() => {
    if (geoData) {
      startTransition(() => {
        if (filteredCompanies.length > 0) {
          const counts = calculateRegionCompanyCounts(
            filteredCompanies,
            geoData
          );
          setRegionCompanyCounts(counts);
          console.log("📊 Region company counts:", counts);
        } else {
          // Reset all regions to 0 when no companies are visible
          const geoDataWithFeatures = geoData as any;
          const resetCounts: Record<string, number> = {};
          if (geoDataWithFeatures && geoDataWithFeatures.features) {
            geoDataWithFeatures.features.forEach((feature: any) => {
              const regionId = String(
                feature.id || feature.properties?.OBJECTID || "unknown"
              );
              resetCounts[regionId] = 0;
            });
          }
          setRegionCompanyCounts(resetCounts);
          console.log("📊 All regions reset to 0 (no visible companies)");
        }
      });
    }
  }, [filteredCompanies, geoData]);

  const handleRegionClick = (region: any) => {
    console.log("Region received in GermanyMapPage:", region); // Debug log
    console.log("Setting selectedRegionId to:", region.id); // Debug log
    startTransition(() => {
      setSelectedRegion(region);
      setSelectedCompany(null);

      // Set selected region ID for highlighting
      setSelectedRegionId(region.id);

      // Open dialog when region is clicked
      setDialogRegion(region);
      setDialogOpen(true);
    });
  };

  const handleCompanyClick = (company: Company) => {
    console.log("Company clicked:", company);
    setSelectedCompany(company);
    setSelectedRegion(null);
    setSelectedRegionId(null);
  };

  const handleRegionSelect = (region: Region) => {
    setDialogRegion(region);
    setDialogOpen(true);
  };

  // Calculate responsive layout based on drawer state
  const getMapLayout = () => {
    if (drawerOpen) {
      return {
        mapFlex: "1 1 600px",
        mapMinWidth: "600px",
        sidePanelFlex: "1 1 300px",
        sidePanelMinWidth: "250px",
        companyPanelFlex: "1 1 350px",
        companyPanelMinWidth: "300px",
      };
    } else {
      return {
        mapFlex: "1 1 800px",
        mapMinWidth: "600px",
        sidePanelFlex: "1 1 400px",
        sidePanelMinWidth: "300px",
        companyPanelFlex: "1 1 400px",
        companyPanelMinWidth: "350px",
      };
    }
  };

  const layout = getMapLayout();

  const primaryTextColor = darkMode
    ? "rgba(255,255,255,0.95)"
    : "rgba(0,0,0,0.87)";
  const secondaryTextColor = darkMode
    ? "rgba(255,255,255,0.7)"
    : "rgba(0,0,0,0.6)";
  const backgroundColor = darkMode ? "#090C37" : "#f7fbff"; // Light, near-white blue for light mode

  const cardBaseStyles = {
    backgroundColor: darkMode
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(255, 255, 255, 0.95)",
    border: darkMode
      ? "1px solid rgba(255, 255, 255, 0.15)"
      : "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: 3,
    boxShadow: darkMode
      ? "0 15px 35px rgba(0, 0, 0, 0.45)"
      : "0 2px 8px rgba(0, 0, 0, 0.1)",
    backdropFilter: "blur(10px)",
  };

  const cardContentStyles = {
    p: 2,
    color: primaryTextColor,
    "& .MuiTypography-root": {
      color: primaryTextColor,
    },
    "& .MuiTypography-root.MuiTypography-body2, & .MuiTypography-root.MuiTypography-caption":
      {
        color: secondaryTextColor,
      },
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#666",
        }}
      >
        {t.loadingMap}
      </div>
    );
  }

  return (
    <Box
      sx={{
        p: drawerOpen ? 1 : 2,
        transition:
          "padding 0.3s ease-in-out, backgroundColor 0.3s ease-in-out",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: backgroundColor,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Logo only */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          color: darkMode ? "common.white" : "common.black",
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="inherit">
          ATN-Heatmap
        </Typography>
        <IconButton
          onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
          sx={{
            color: darkMode ? "common.white" : "common.black",
            "&:hover": {
              backgroundColor: darkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
            },
          }}
        >
          <Settings />
        </IconButton>
        <Menu
          anchorEl={settingsMenuAnchor}
          open={Boolean(settingsMenuAnchor)}
          onClose={() => setSettingsMenuAnchor(null)}
          PaperProps={{
            sx: {
              backgroundColor: darkMode
                ? "rgba(9, 12, 55, 0.95)"
                : "rgba(255, 255, 255, 0.95)",
              color: darkMode ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.87)",
              border: darkMode
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.1)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.45)",
              minWidth: 200,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setDarkMode(!darkMode);
              setSettingsMenuAnchor(null);
            }}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography>{t.darkMode}</Typography>
            <Switch checked={darkMode} />
          </MenuItem>
          <MenuItem
            onClick={() => {
              setLanguage(language === 'en' ? 'de' : 'en');
              // Don't close menu when switching language
            }}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography>{t.language}</Typography>
              <Box
                component="img"
                src={language === 'en' ? 'https://flagcdn.com/w20/gb.png' : '/Flag_of_Germany.svg.png'}
                alt={language === 'en' ? 'English' : 'Deutsch'}
                sx={{
                  width: 20,
                  height: 15,
                  objectFit: 'cover',
                  borderRadius: '2px',
                  border: `1px solid ${darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
                }}
              />
            </Box>
            <Switch checked={language === 'de'} />
          </MenuItem>
        </Menu>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isSideCollapsed
            ? "1fr"
            : { xs: "1fr", md: "380px 1fr" },
          gap: isSideCollapsed ? 0 : 2,
          alignItems: "stretch",
          height: "100%",
          maxHeight: "100%",
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Left panel: controls & results */}
        {!isSideCollapsed && (
          <Box
            sx={{
              minWidth: { md: 320 },
              overflow: "hidden",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Controls */}
            <Card sx={{ ...cardBaseStyles, mb: 2, flexShrink: 0 }}>
              <CardContent sx={{ ...cardContentStyles }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<CloudUpload />}
                    onClick={() => setImportDialogOpen(true)}
                    disabled={isImporting}
                    sx={{
                      backgroundColor: "#34A936",
                      color: "#FFFFFF",
                      fontWeight: 600,
                      "&:hover:not(:disabled)": {
                        backgroundColor: "#2A8A2B",
                      },
                      "&:disabled": {
                        backgroundColor: "#4a4d63 !important",
                        color: "rgba(255, 255, 255, 0.5) !important",
                        cursor: "not-allowed",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#4a4d63 !important",
                        color: "rgba(255, 255, 255, 0.5) !important",
                        cursor: "not-allowed",
                      },
                      "&.MuiButton-contained.Mui-disabled": {
                        backgroundColor: "#4a4d63 !important",
                        color: "rgba(255, 255, 255, 0.5) !important",
                      },
                    }}
                  >
                    {t.importCompanies}
                  </Button>

                  {/* Progress Bar for Import */}
                  {isImporting && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        {importStatus}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box sx={{ width: "100%" }}>
                          <LinearProgress
                            variant="determinate"
                            value={importProgress}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: "rgba(0,0,0,0.1)",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 4,
                              },
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ minWidth: 40, color: "rgba(255,255,255,0.7)" }}
                        >
                          {Math.round(importProgress)}%
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {companies.length > 0 && (
                    <TextField
                      size="small"
                      label={t.searchCompanies}
                      placeholder={t.searchPlaceholder}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputLabelProps={{
                        sx: { color: secondaryTextColor },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: secondaryTextColor }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchTerm("")}
                              edge="end"
                              sx={{ color: secondaryTextColor }}
                            >
                              <Clear />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          transition: "all 0.2s ease",
                          color: primaryTextColor,
                          backgroundColor: darkMode
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(255,255,255,1)",
                          "& fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.3)"
                              : "rgba(0,0,0,0.23)",
                          },
                          "&:hover fieldset": {
                            borderColor: "#34A936",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#34A936",
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#34A936",
                        },
                      }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>

            {searchTerm && filteredCompanies.length > 0 && (
              <Card sx={{ ...cardBaseStyles, mb: 2, flexShrink: 0 }}>
                <CardContent sx={{ ...cardContentStyles }}>
                  <Typography variant="h6" gutterBottom>
                    {t.searchResults} ({filteredCompanies.length} {t.companiesFound})
                  </Typography>
                  <Box
                    className="custom-scrollbar"
                    sx={{ maxHeight: "calc(100vh - 400px)", overflowY: "auto" }}
                  >
                    <List dense>
                      {filteredCompanies.slice(0, 20).map((company, index) => (
                        <ListItem
                          key={company.id}
                          sx={{
                            px: 2,
                            py: 1.5,
                            mb: 1,
                            borderRadius: 2,
                            background: darkMode
                              ? "linear-gradient(135deg, rgba(17,25,56,0.95), rgba(7,14,34,0.92))"
                              : "rgba(255,255,255,1)",
                            border: darkMode
                              ? "1px solid rgba(255,255,255,0.06)"
                              : "1px solid rgba(0,0,0,0.08)",
                            boxShadow: darkMode
                              ? "0 12px 25px rgba(0,0,0,0.35)"
                              : "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 46 }}>
                            <Business sx={{ color: secondaryTextColor }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={company.name}
                            primaryTypographyProps={{
                              sx: { color: primaryTextColor },
                            }}
                            secondary={
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ color: secondaryTextColor }}
                                >
                                  {company.address}
                                </Typography>
                                <Chip
                                  label={company.region || "Unknown Region"}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ mt: 0.5 }}
                                />
                                {(company.topics || company.membership) && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.75,
                                      mt: 0.75,
                                    }}
                                  >
                                    {company.topics && (
                                      <Chip
                                        label={company.topics}
                                        size="small"
                                        sx={{
                                          height: 22,
                                          fontSize: "0.7rem",
                                          ...getColoredChipStyles(
                                            company.topics,
                                            topicChipStyles
                                          ),
                                        }}
                                      />
                                    )}
                                    {company.membership && (
                                      <Chip
                                        label={company.membership}
                                        size="small"
                                        sx={{
                                          height: 22,
                                          fontSize: "0.7rem",
                                          ...getColoredChipStyles(
                                            company.membership,
                                            membershipChipStyles
                                          ),
                                        }}
                                      />
                                    )}
                                  </Box>
                                )}
                                {/* Displayed flag intentionally hidden in list */}
                              </Box>
                            }
                          />
                          <Checkbox
                            checked={companyVisibility[company.id] !== false}
                            onChange={(e) => {
                              const newVisibility = {
                                ...companyVisibility,
                                [company.id]: e.target.checked,
                              };
                              setCompanyVisibility(newVisibility);
                            }}
                            size="small"
                            sx={{
                              color: "#34A936",
                              "&.Mui-checked": {
                                color: "#34A936",
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </CardContent>
              </Card>
            )}

            {searchTerm && filteredCompanies.length === 0 && (
              <Card sx={{ ...cardBaseStyles, mb: 2, flexShrink: 0 }}>
                <CardContent sx={{ ...cardContentStyles }}>
                  <Typography
                    variant="body1"
                    sx={{
                      textAlign: "center",
                      py: 2,
                      color: secondaryTextColor,
                    }}
                  >
                    {t.noCompaniesFound} "{searchTerm}"
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Companies List */}
            {companies.length > 0 && !searchTerm && (
              <Card
                sx={{
                  ...cardBaseStyles,
                  mb: 2,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <CardContent
                  sx={{
                    ...cardContentStyles,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {t.companies} ({companies.length})
                  </Typography>

                  <Box
                    className="custom-scrollbar"
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      minHeight: 0,
                      maxHeight: "calc(100vh - 400px)",
                    }}
                  >
                    <List dense>
                      {companies.map((company, index) => (
                        <ListItem
                          key={company.id}
                          sx={{
                            px: 2,
                            py: 1.5,
                            mb: 1,
                            borderRadius: 2,
                            background: darkMode
                              ? "linear-gradient(135deg, rgba(15,20,45,0.95), rgba(5,9,22,0.9))"
                              : "rgba(255,255,255,1)",
                            border: darkMode
                              ? "1px solid rgba(255,255,255,0.05)"
                              : "1px solid rgba(0,0,0,0.08)",
                            boxShadow: darkMode
                              ? "0 12px 28px rgba(0,0,0,0.35)"
                              : "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.8rem",
                                fontWeight: "bold",
                                color: "#0A1C34",
                                background:
                                  "linear-gradient(145deg, #5BE49A, #34A936)",
                                boxShadow: "0 6px 14px rgba(52,169,54,0.45)",
                              }}
                            >
                              {index + 1}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                sx={{ color: primaryTextColor }}
                              >
                                {company.name}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    mb: 0.5,
                                    color: secondaryTextColor,
                                  }}
                                >
                                  {company.address}
                                </Typography>
                                <Chip
                                  label={company.region || "Unknown Region"}
                                  size="small"
                                  sx={{
                                    backgroundColor: "rgba(52,169,54,0.15)",
                                    color: "#34A936",
                                    fontSize: "0.7rem",
                                    height: 20,
                                  }}
                                />
                                {(company.topics || company.membership) && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.75,
                                      mt: 0.75,
                                    }}
                                  >
                                    {company.topics && (
                                      <Chip
                                        label={company.topics}
                                        size="small"
                                        sx={{
                                          height: 22,
                                          fontSize: "0.7rem",
                                          ...getColoredChipStyles(
                                            company.topics,
                                            topicChipStyles
                                          ),
                                        }}
                                      />
                                    )}
                                    {company.membership && (
                                      <Chip
                                        label={company.membership}
                                        size="small"
                                        sx={{
                                          height: 22,
                                          fontSize: "0.7rem",
                                          ...getColoredChipStyles(
                                            company.membership,
                                            membershipChipStyles
                                          ),
                                        }}
                                      />
                                    )}
                                  </Box>
                                )}
                                {/* Displayed flag controlled via checkbox, no text */}
                              </Box>
                            }
                          />
                          <Checkbox
                            checked={companyVisibility[company.id] !== false}
                            onChange={(e) => {
                              setCompanyVisibility((prev) => ({
                                ...prev,
                                [company.id]: e.target.checked,
                              }));
                            }}
                            size="small"
                            sx={{
                              color: "#34A936",
                              "&.Mui-checked": {
                                color: "#34A936",
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Show All / Hide All buttons at bottom */}
                  <Box sx={{ display: "flex", gap: 1, mt: 2, flexShrink: 0 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        const newVisibility: Record<string, boolean> = {};
                        companies.forEach((company) => {
                          newVisibility[company.id] = true;
                        });
                        setCompanyVisibility((prev) => ({
                          ...prev,
                          ...newVisibility,
                        }));
                      }}
                      sx={{ flex: 1 }}
                    >
                      {t.showAll}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const newVisibility: Record<string, boolean> = {};
                        companies.forEach((company) => {
                          newVisibility[company.id] = false;
                        });
                        setCompanyVisibility((prev) => ({
                          ...prev,
                          ...newVisibility,
                        }));
                      }}
                      sx={{ flex: 1 }}
                    >
                      {t.hideAll}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* Right panel: map only */}
        <Box
          sx={{
            minWidth: { xs: "100%", md: 600 },
            height: "100%",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Loading indicator for meeting points */}
          {isLoadingMeetingPoints && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Mosaic color="#34A936" size="medium" text="" textColor="" />
            </Box>
          )}
          <MapContainer
            center={[51.1657, 10.4515]}
            zoom={6}
            zoomControl={true}
            scrollWheelZoom={true}
            doubleClickZoom={true}
            style={{ height: "100%", width: "100%" }}
          >
            {/* Recalculate size after collapse/expand */}
            <MapResizeInvalidator dependency={isSideCollapsed} />
            <TileLayer
              url={
                darkMode
                  ? "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                  : "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
              }
              attribution="&copy; Esri — Esri, DeLorme, NAVTEQ"
              key={darkMode ? "dark" : "light"}
            />
            <MapBoundsFitter geoData={geoData} />
            <AlternativeGeoJSONLayer
              data={geoData || null}
              onRegionClick={handleRegionClick}
              onCompanyClick={handleCompanyClick}
              selectedRegionId={selectedRegionId || undefined}
              showCompanies={showCompanies}
              companies={filteredCompanies}
              onRegionSelect={handleRegionSelect}
              regionCompanyCounts={regionCompanyCounts}
            />
            {/* Meeting points - always visible on map */}
            <MeetingPointsMarkers
              meetingPoints={meetingPoints}
              onCompanyClick={handleCompanyClick}
            />
            {/* Regular companies */}
            <ClusterMarkers
              companies={filteredCompanies}
              showCompanies={showCompanies}
              onCompanyClick={handleCompanyClick}
            />

            <div
              className={`map-legend ${
                darkMode ? "map-legend-dark" : "map-legend-light"
              }`}
            >
              <div className="legend-title">{t.companyCount}</div>
              <div className="legend-items">
                <div className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#090C37" }}
                  ></div>
                  <span>0</span>
                </div>
                <div className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#13174A" }}
                  ></div>
                  <span>1-20</span>
                </div>
                <div className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#1E225D" }}
                  ></div>
                  <span>21-40</span>
                </div>
                <div className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#283D70" }}
                  ></div>
                  <span>41-60</span>
                </div>
                <div className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#335984" }}
                  ></div>
                  <span>61-80</span>
                </div>
                <div className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#3D7597" }}
                  ></div>
                  <span>81-100</span>
                </div>
                <div className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#4891AA" }}
                  ></div>
                  <span>101-120</span>
                </div>
                <div className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#52ACBD" }}
                  ></div>
                  <span>121-140</span>
                </div>
                <div className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#5DC8D1" }}
                  ></div>
                  <span>141-160</span>
                </div>
                <div className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#67E4E4" }}
                  ></div>
                  <span>160+</span>
                </div>
              </div>
            </div>
          </MapContainer>
        </Box>
      </Box>

      {/* Material-UI Dialog for Region Details */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode
              ? "rgba(9,12,55,0.95)"
              : "rgba(255,255,255,0.95)",
            color: primaryTextColor,
            border: darkMode
              ? "1px solid rgba(255,255,255,0.15)"
              : "1px solid rgba(0,0,0,0.1)",
            boxShadow: darkMode
              ? "0 25px 45px rgba(0,0,0,0.7)"
              : "0 8px 24px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle sx={{ color: primaryTextColor }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              width={20}
              height={20}
              borderRadius="50%"
              bgcolor={dialogRegion?.color || "#E0E0E0"}
            />
            <Typography variant="h6">
              {dialogRegion?.name || t.regionDetails}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ color: primaryTextColor }}>
          <Box py={2}>
            <Typography variant="body1" gutterBottom color="inherit">
              <strong>{t.region}:</strong>{" "}
              {dialogRegion?.nameEn || dialogRegion?.name}
            </Typography>

            {/* <Typography variant="body1" gutterBottom>
              <strong>Population:</strong> {dialogRegion?.population?.toLocaleString() || 'N/A'}
            </Typography> */}

            <Box display="flex" alignItems="center" gap={1} my={2}>
              <Typography variant="body1" color="inherit">
                <strong>Companies:</strong>
              </Typography>
              <Chip
                label={`${dialogRegion?.companies?.length || 0} companies`}
                color="primary"
                size="small"
              />
            </Box>

            <Divider
              sx={{
                my: 2,
                borderColor: darkMode
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.12)",
              }}
            />

            {dialogRegion?.companies && dialogRegion.companies.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom color="inherit">
                  Company List:
                </Typography>
                {dialogRegion.companies.slice(0, 5).map((company, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ color: secondaryTextColor }}
                  >
                    • {company.name} - {company.address}
                  </Typography>
                ))}
                {dialogRegion.companies.length > 5 && (
                  <Typography
                    variant="body2"
                    sx={{ color: secondaryTextColor }}
                  >
                    ... and {dialogRegion.companies.length - 5} more companies
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: secondaryTextColor }}>
                No companies found in this region.
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: darkMode
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.1)",
            backgroundColor: darkMode
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.02)",
          }}
        >
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              color: secondaryTextColor,
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.05)",
              },
            }}
          >
            {t.close}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <ImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImportCompanies}
        darkMode={darkMode}
        language={language}
      />

      {/* Failed Geocoding Dialog */}
      <Dialog
        open={failedGeocodingDialogOpen}
        onClose={() => {}} // Prevent closing by clicking outside
        maxWidth="sm"
        sx={{
          "& .MuiPaper-root": {
            background: darkMode
              ? "linear-gradient(145deg, #0D1234, #050714)"
              : "linear-gradient(145deg, #FFFFFF, #F5F5F5)",
            color: darkMode ? "#fff" : "#000",
            borderRadius: 3,
            border: darkMode
              ? "1px solid rgba(200, 162, 78, 0.6)"
              : "1px solid rgba(0, 0, 0, 0.2)",
            boxShadow: darkMode
              ? "0 25px 60px rgba(0, 0, 0, 0.65)"
              : "0 8px 24px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: darkMode
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.1)",
            pb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ErrorIcon sx={{ color: "#ff6b6b" }} />
          <Typography variant="h6">Company Location Not Found</Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            color: darkMode ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
            pt: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            }}
          >
            Unable to find location for the following companies:
          </Typography>
          <Box
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              pr: 1,
              "&::-webkit-scrollbar": {
                width: 6,
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255,255,255,0.05)",
                borderRadius: 3,
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,107,107,0.7)",
                borderRadius: 3,
              },
            }}
          >
            <List dense>
              {failedCompanies.map((company, index) => (
                <ListItem
                  key={index}
                  sx={{
                    px: 1.5,
                    py: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    background: darkMode
                      ? "rgba(255,107,107,0.1)"
                      : "rgba(255,107,107,0.15)",
                    border: darkMode
                      ? "1px solid rgba(255,107,107,0.2)"
                      : "1px solid rgba(255,107,107,0.3)",
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          color: darkMode
                            ? "rgba(255,255,255,0.95)"
                            : "rgba(0,0,0,0.95)",
                        }}
                      >
                        {company.name}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.25,
                          color: darkMode
                            ? "rgba(255,255,255,0.6)"
                            : "rgba(0,0,0,0.6)",
                        }}
                      >
                        {company.address}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: darkMode
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.1)",
            px: 3,
            py: 1.5,
          }}
        >
          <Button
            onClick={() => {
              setFailedGeocodingDialogOpen(false);
              setFailedCompanies([]);
            }}
            variant="contained"
            sx={{
              backgroundColor: "#34A936",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#2A8A2B",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Draggable floating collapse toggle */}
      <DraggableCollapseButton
        collapsed={isSideCollapsed}
        onToggle={() => setIsSideCollapsed((v) => !v)}
      />
    </Box>
  );
};

export default GermanyMapPage;

// CSS Styles for Map Legend Overlay
const mapLegendStyles = `
  .map-legend {
    position: absolute;
    top: 10px;
    right: 10px;
    padding: 15px;
    border-radius: 12px;
    z-index: 1000;
    font-family: 'Roboto', sans-serif;
    min-width: 140px;
    backdrop-filter: blur(6px);
    transition: all 0.3s ease;
  }
  
  .map-legend-dark {
    background: rgba(9, 12, 55, 0.9);
    box-shadow: 0 15px 35px rgba(0,0,0,0.45);
    border: 1px solid rgba(255,255,255,0.2);
    color: rgba(255,255,255,0.9);
  }
  
  .map-legend-light {
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    border: 1px solid rgba(0,0,0,0.1);
    color: rgba(0,0,0,0.87);
  }
  
  .legend-title {
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 8px;
    color: #34A936;
    text-align: center;
  }
  
  .legend-items {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  
  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
  }
  
  .map-legend-dark .legend-item {
    color: rgba(255,255,255,0.9);
  }
  
  .map-legend-light .legend-item {
    color: rgba(0,0,0,0.87);
  }
  
  .legend-color {
    width: 16px;
    height: 16px;
    border-radius: 2px;
    flex-shrink: 0;
  }
  
  .map-legend-dark .legend-color {
    border: 1px solid ${MAP_COLORS.border};
  }
  
  .map-legend-light .legend-color {
    border: 1px solid rgba(0,0,0,0.2);
  }
  
  .map-legend-dark .legend-item span {
    color: rgba(255,255,255,0.85) !important;
    font-weight: 500;
  }
  
  .map-legend-light .legend-item span {
    color: rgba(0,0,0,0.87) !important;
    font-weight: 500;
  }

  .legend-line {
    width: 18px;
    height: 2px;
    background: ${MAP_COLORS.border};
  }

  /* Make Leaflet attribution text white for dark theme */
  .leaflet-control-attribution,
  .leaflet-control-attribution a { color: #000 !important; text-shadow: none; }

  /* Force white labels/tooltips/popups inside the map */
  .leaflet-container .leaflet-tooltip,
  .leaflet-container .leaflet-tooltip *,
  .leaflet-container .leaflet-popup-content,
  .leaflet-container .leaflet-popup-content * {
    color: #000 !important;
  }

  /* Zoom control and scale in white */
  .leaflet-control-zoom a,
  .leaflet-control-scale-line {
    color: #000 !important;
    border-color: rgba(0,0,0,0.4) !important;
    background: rgba(255,255,255,0.8) !important;
  }

  /* Custom scrollbar - green theme, thinner */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #34A936;
    border-radius: 3px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #2a8a2b;
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #34A936 #f1f1f1;
  }
`;

// Inject styles into document
if (typeof document !== "undefined") {
  const existingStyle = document.getElementById("map-legend-styles");
  if (!existingStyle) {
    const styleSheet = document.createElement("style");
    styleSheet.id = "map-legend-styles";
    styleSheet.textContent = mapLegendStyles;
    document.head.appendChild(styleSheet);
  }
}

// Draggable floating button to toggle side layout
function DraggableCollapseButton({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragging || !buttonRef.current) return;
      const width = window.innerWidth;
      const height = window.innerHeight;
      const rect = buttonRef.current.getBoundingClientRect();
      const margin = 8;
      const newLeft = Math.min(
        Math.max(margin, e.clientX - rect.width / 2),
        width - rect.width - margin
      );
      const newTop = Math.min(
        Math.max(margin, e.clientY - rect.height / 2),
        height - rect.height - margin
      );
      setPosition({ top: newTop, left: newLeft });
    };
    const handleMouseUp = () => setDragging(false);
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging]);

  return (
    <div
      ref={buttonRef}
      onMouseDown={(e) => {
        // Start dragging only if not clicking the icon action area
        setDragging(true);
        e.preventDefault();
      }}
      style={{
        position: "fixed",
        zIndex: 2000,
        right: position ? undefined : 48,
        bottom: position ? undefined : 28,
        top: position ? position.top : undefined,
        left: position ? position.left : undefined,
        cursor: dragging ? "grabbing" : "grab",
      }}
    >
      <Tooltip title={collapsed ? "Mở layout" : "Thu gọn layout"}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          sx={{
            bgcolor: "primary.light",
            color: "white",
            "&:hover": { bgcolor: "primary.main" },
            boxShadow: 3,
            width: 52,
            height: 52,
          }}
        >
          {collapsed ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>
      </Tooltip>
    </div>
  );
}
